import { forwardRef } from 'react'

// Page Components
import PrescriptionMakingPage from 'src/mentor/components/PrescriptionMakingPage'
import TherapyReportMakingPage from 'src/mentor/components/TherapyReportMakingPage'
import ScreeningReportMakingPage from 'src/mentor/components/ScreeningReportMakingPage'
import AssessmentReportMakingPage from 'src/mentor/components/AssessmentReport/AssessmentReportMakingPage'
import VitalsListingPage from 'src/mentor/components/VitalsListingPage'
import {
  ActivityPlansListing,
  LSInvestigationsPageComponent,
} from '../../LiveSessionPageComponents'
import RXDrawerComponent from 'src/consumer/components/LiveSessionPage/components/RXDrawerComponent/RXDrawerComponent'
import PrintRxDrawerComponent from 'src/consumer/components/LiveSessionPage/components/PrintRxDrawerComponent/PrintRxDrawerComponent'
import ReportPreviewComponent from 'src/consumer/components/LiveSessionPage/components/ReportPreviewComponent/ReportPreviewComponent'
import ScreeningReportDisplay from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/ScreeningReportDisplay'
import ScreeningReportDrawerComponent from 'src/consumer/components/LiveSessionPage/components/ScreeningReportDrawerComponent/ScreeningReportDrawerComponent'
import LSPastVisitReportsComponent from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/LSPastVisitReportsComponent'

/**
 * LSRenderComponent
 *
 * Renders different live session menu items based on selectedMenuItem.
 * Follows SRP by handling only the rendering logic for session content.
 *
 * @param {Object} props - Component properties
 * @param {string} props.selectedMenuItem - Current menu item to render
 * @param {Object} props.sessionData - Session and event data
 * @param {Object} props.mentorData - Mentor profile data
 * @param {Object} props.teacherData - Teacher profile data
 * @param {Object} props.prescriptionState - Prescription state and setters
 * @param {Object} props.callbacks - Event handler functions
 * @param {Object} props.refs - Forwarded refs for components that need them
 */
const LSRenderComponent = forwardRef(
  (
    {
      selectedMenuItem,
      sessionData,
      mentorData,
      teacherData,
      prescriptionState,
      callbacks,
      onClose = () => {},
      menuItemsList = [], // Add menuItemsList prop to determine role
    },
    refs,
  ) => {
    const { prescriptionRef, therapyReportRef, screeningReportRef, assessmentReportRef } = refs || {}

    // Common session data destructuring
    const {
      bookedEventFinalData,
      auxiliaryData,
      moveToPrintPrescriptionScreen,
      setMoveToPrintPrescriptionScreen,
      newWantedMenuItemId,
      setNewWantedMenuItemId,
      setSelectedMenuItem,
      drawerOpenHandler, // Get drawerOpenHandler from sessionData
    } = sessionData

    // Prescription state destructuring
    const { prescriptionContentValue, setPrescriptionContentValue } = prescriptionState

    // Callbacks destructuring
    const { handleLeaveRoom, setupPage } = callbacks

    // Handler for RxPrint button - opens Rx drawer and triggers PRINT ALL
    const handleRxPrint = () => {
      console.log('[RxPrint] Opening Rx drawer and triggering PRINT ALL')
      
      // Flag to prevent duplicate print calls
      let printTriggered = false
      
      // Open Rx drawer first - need to both set selected menu item AND open drawer
      setSelectedMenuItem('rx')
      
      // Also trigger drawer opening if handler is available
      if (drawerOpenHandler && typeof drawerOpenHandler === 'function') {
        drawerOpenHandler('rx')
      }
      
      // Helper function to trigger print (prevents duplicate calls)
      const triggerPrint = () => {
        if (printTriggered) {
          console.log('[RxPrint] Print already triggered, skipping duplicate call')
          return
        }
        
        if (prescriptionRef?.current?.saveAndPrint) {
          printTriggered = true
          console.log('[RxPrint] Triggering PRINT ALL')
          // Trigger PRINT ALL via ref (this will be handled by Rx drawer's handleSaveAndPrint)
          prescriptionRef.current.saveAndPrint(null).catch(err => {
            console.error('[RxPrint] Error triggering PRINT ALL:', err)
            printTriggered = false // Reset on error so user can retry
          })
        }
      }
      
      // Wait for drawer to open and Rx component to mount, then trigger PRINT ALL
      // The Rx drawer uses prescriptionRef to call saveAndPrint, so we need to wait
      // for the drawer to be fully mounted before triggering
      const checkAndPrint = setInterval(() => {
        // Check if prescriptionRef has saveAndPrint method available
        // This should work because Rx drawer uses the same prescriptionRef
        if (prescriptionRef?.current?.saveAndPrint) {
          clearInterval(checkAndPrint)
          console.log('[RxPrint] Rx drawer ready')
          triggerPrint()
        }
      }, 150) // Check every 150ms
      
      // Timeout after 2 seconds if Rx drawer doesn't become ready
      const timeoutId = setTimeout(() => {
        clearInterval(checkAndPrint)
        if (!printTriggered) {
          console.log('[RxPrint] Timeout reached, attempting to trigger PRINT ALL')
          triggerPrint()
        } else {
          console.log('[RxPrint] Print already triggered, skipping timeout fallback')
        }
      }, 2000)
      
      // Store timeout ID for potential cleanup (though this function doesn't have cleanup)
      // The timeout will naturally complete or be cleared by the interval
    }

    // Render different components based on selected menu item
    switch (selectedMenuItem) {
      case 'prescription':
        return (
          <PrescriptionMakingPage
            ref={prescriptionRef}
            propEventId={bookedEventFinalData?.id}
            propPatientId={bookedEventFinalData?.patientId}
            propClinicId={bookedEventFinalData?.clinicId}
            propClinicBrandId={bookedEventFinalData?.clinicBrandId || ''}
            propMentorProfileData={mentorData}
            honorPropValue
            showPatientDetailsInHeader
            propValue={prescriptionContentValue}
            setPropValue={setPrescriptionContentValue}
            propBookedEventInfo={bookedEventFinalData}
            auxiliaryDataFromProps={auxiliaryData}
            getProcessAndSetEventData={setupPage}
            moveToPrintPrescriptionScreen={moveToPrintPrescriptionScreen}
            setMoveToPrintPrescriptionScreen={setMoveToPrintPrescriptionScreen}
            afterSaveFunction={(markAsCompleted = false) => {
              if (newWantedMenuItemId === 'end') {
                handleLeaveRoom(markAsCompleted)
                setNewWantedMenuItemId(undefined)
              } else if (newWantedMenuItemId) {
                // Only change menu item if there's a valid new item to navigate to
                setSelectedMenuItem(newWantedMenuItemId)
              }
              // If newWantedMenuItemId is undefined, stay on current page (don't change selectedMenuItem)
            }}
            onRxPrint={handleRxPrint}
          />
        )
      case 'consultation_screening_report':
        return (
          <ScreeningReportDrawerComponent
            auxiliaryData={auxiliaryData}
            bookedEventInfo={bookedEventFinalData}
            mentorProfileData={mentorData}
          />
        )

      case 'patient_history':
        // Determine role based on the primary menu item (prescription = doctor, therapy_report = therapist)
        // Get the primary menu item (first non-video_call item)
        const primaryMenuItem = menuItemsList?.find((item) => item?.id !== 'video_call')
        const roleForHistory = primaryMenuItem?.id === 'prescription' ? 'doctor' : 'therapist'
        return (
          <LSPastVisitReportsComponent
            patientId={bookedEventFinalData?.patientId}
            referenceBookedEventId={bookedEventFinalData?.id}
            methodCallWhenNoReportDataIsFound={() => {
              // Don't close drawer when no data is found - just log it
              console.log('No report data found for patient history')
            }}
            addTopMargins
            role={roleForHistory}
            showIndexSidebar
          />
        )

      case 'investigations':
        return (
          <LSInvestigationsPageComponent
            patientId={bookedEventFinalData?.patientId}
            clinicBrandId={bookedEventFinalData?.clinicBrandId}
            mentorId={bookedEventFinalData?.mentorId}
            name={bookedEventFinalData?.userName}
          />
        )

      case 'therapy_report':
        return (
          <TherapyReportMakingPage
            ref={therapyReportRef}
            propEventId={bookedEventFinalData?.id}
            propPatientId={bookedEventFinalData?.patientId}
            propClinicId={bookedEventFinalData?.clinicId}
            propClinicBrandId={bookedEventFinalData?.clinicBrandId}
            propMentorProfileData={mentorData}
            leaveRoom={handleLeaveRoom}
            honorPropValue
            propValue={prescriptionContentValue}
            setPropValue={setPrescriptionContentValue}
            propBookedEventInfo={bookedEventFinalData}
            auxiliaryDataFromProps={auxiliaryData}
            getProcessAndSetEventData={setupPage}
          />
        )

      case 'screening_report':
        return (
          <ScreeningReportMakingPage
            ref={screeningReportRef}
            propEventId={bookedEventFinalData?.id}
            propPatientId={bookedEventFinalData?.patientId}
            propClinicId={bookedEventFinalData?.clinicId}
            propMentorProfileData={mentorData}
            propBookedEventInfo={bookedEventFinalData}
            auxiliaryDataFromProps={auxiliaryData}
            getProcessAndSetEventData={setupPage}
            leaveRoom={handleLeaveRoom}
          />
        )

      case 'assessment_report':
        return (
          <AssessmentReportMakingPage
            ref={assessmentReportRef}
            propEventId={bookedEventFinalData?.id}
            propPatientId={bookedEventFinalData?.patientId}
            propClinicId={bookedEventFinalData?.clinicId}
            propClinicBrandId={bookedEventFinalData?.clinicBrandId || ''}
            propMentorProfileData={mentorData}
            propBookedEventInfo={bookedEventFinalData}
            leaveRoom={handleLeaveRoom}
            auxiliaryDataFromProps={auxiliaryData}
            getProcessAndSetEventData={setupPage}
            chamberRoomId={undefined}
          />
        )

      case 'vitals':
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              margin: '0 auto',
              padding: '20px',
              maxWidth: '576px',
            }}
          >
            <VitalsListingPage
              patientId={bookedEventFinalData?.patientId}
              clinicId={bookedEventFinalData?.clinicId}
            />
          </div>
        )

      case 'activity_plans':
        return (
          <div style={{ padding: '20px' }}>
            <ActivityPlansListing
              mentorId={teacherData?.id}
              clientName={bookedEventFinalData?.userName}
              clientId={bookedEventFinalData?.userId}
            />
          </div>
        )
      case 'rx':
        return (
          <div style={{ padding: '0px' }}> {/* Remove padding for full width prescription report */}
            <RXDrawerComponent
              mentorProfileData={mentorData}
              auxiliaryData={auxiliaryData}
              bookedEventData={bookedEventFinalData}
              prescriptionRef={prescriptionRef}
              onClose={onClose}
            />
          </div>
        )
      case 'print_rx':
        return (
          <div style={{ padding: '0px', margin: '0px', width: '100%', boxSizing: 'border-box' }}> {/* Remove padding for full width prescription report */}
            <PrintRxDrawerComponent
              prescriptionRef={prescriptionRef}
              onClose={onClose}
            />
          </div>
        )
      case 'assessments':
        // Practitioner assessment UI is in PractitionerAssessmentDrawer; main area stays empty
        return null

      case 'report-preview':
        return (
          <div style={{ padding: '20px' }}>
            {/* DEBUG MARKER - Should be visible at top of Report Preview */}
            <div style={{
              background: 'red',
              color: 'white',
              padding: '15px',
              marginBottom: '20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}>
              🔴 REPORT PREVIEW COMPONENT - Event Type: {bookedEventFinalData?.eventType}
            </div>
            <ReportPreviewComponent
              eventType={bookedEventFinalData?.eventType}
              mentorProfileData={mentorData}
              auxiliaryData={auxiliaryData}
              bookedEventData={bookedEventFinalData}
              therapyReportRef={therapyReportRef}
              screeningReportRef={screeningReportRef}
              assessmentReportRef={assessmentReportRef}
              getProcessAndSetEventData={setupPage}
            />
          </div>
        )

      default:
        return null
    }
  },
)

LSRenderComponent.displayName = 'LSRenderComponent'

export default LSRenderComponent
